import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import red from '@material-ui/core/colors/red';
import API_Service, { axiosDefaultConfig } from '../../Services/API_Service';
import { req_get_preference, req_save_preference } from '../../Helper/Constants';
import { de } from '../../Helper/Helper';

const useStyles = makeStyles((theme) => ({
	sectionLabel: {
		fontSize: theme.typography.pxToRem(13),
		fontWeight: theme.typography.fontWeightMedium,
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(0.5)
	},
	columnGrid: {
		display: 'flex',
		flexWrap: 'wrap'
	},
	columnItem: {
		width: '50%',
		marginRight: 0
	},
	cancelText: {
		color: red[400]
	}
}));

export default function ExportColumnDialog(props) {
	const { open, setClose, language, client, pageKey, sections, onGenerate } = props;
	const classes = useStyles();
	const [selected, setSelected] = useState({});

	const defaults = useMemo(() => {
		const map = {};
		sections.forEach(section => {
			section.columns.forEach(col => {
				map[col.key] = true;
			});
		});
		return map;
	}, [sections]);

	useEffect(() => {
		if (!open) return;
		setSelected(defaults);
		if (!client || !pageKey) return;
		API_Service.get(
			req_get_preference + '?page_key=' + encodeURIComponent(pageKey) + '&preference_type=export_columns',
			axiosDefaultConfig(client)
		).then(({ data }) => {
			if (data.result === 1 && data.extraData && data.extraData.preference_json) {
				try {
					const saved = JSON.parse(data.extraData.preference_json);
					setSelected(prev => {
						const merged = { ...prev };
						sections.forEach(section => {
							section.columns.forEach(col => {
								if (!col.locked && typeof saved[col.key] === 'boolean') {
									merged[col.key] = saved[col.key];
								}
							});
						});
						return merged;
					});
				} catch (e) {
					de('ExportColumnDialog: invalid preference json');
				}
			}
		}).catch(() => {
			de('ExportColumnDialog: preference load failed');
		});
	}, [open, client, pageKey, sections, defaults]);

	const toggle = (col) => {
		if (col.locked) return;
		setSelected(prev => ({ ...prev, [col.key]: !prev[col.key] }));
	};

	const handleReset = () => {
		setSelected(defaults);
	};

	const handleGenerate = () => {
		const keys = [];
		const prefMap = {};
		sections.forEach(section => {
			section.columns.forEach(col => {
				const isOn = col.locked ? true : !!selected[col.key];
				prefMap[col.key] = isOn;
				if (isOn) {
					keys.push(col.key);
				}
			});
		});
		if (client && pageKey) {
			const formdata = new FormData();
			formdata.append('page_key', pageKey);
			formdata.append('preference_type', 'export_columns');
			formdata.append('preference_json', JSON.stringify(prefMap));
			API_Service.post(req_save_preference, formdata, axiosDefaultConfig(client)).catch(() => {
				de('ExportColumnDialog: preference save failed');
			});
		}
		onGenerate(keys);
	};

	return (
		<Dialog maxWidth="sm" fullWidth open={open} onClose={() => setClose()}>
			<DialogTitle>{language.export_column_settings}</DialogTitle>
			<DialogContent dividers>
				{sections.map((section, idx) => (
					<div key={String(idx)}>
						<Typography className={classes.sectionLabel}>{section.label}</Typography>
						<div className={classes.columnGrid}>
							{section.columns.map(col => (
								<FormControlLabel
									key={col.key}
									className={classes.columnItem}
									control={
										<Checkbox
											color="primary"
											checked={col.locked ? true : !!selected[col.key]}
											disabled={!!col.locked}
											onChange={() => toggle(col)}
										/>
									}
									label={col.label}
								/>
							))}
						</div>
					</div>
				))}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleReset} color="primary">
					<Typography component="p">{language.reset_selection}</Typography>
				</Button>
				<Button onClick={() => setClose()} color="primary">
					<Typography component="p" className={classes.cancelText}>{language.cancel}</Typography>
				</Button>
				<Button onClick={handleGenerate} color="primary">
					<Typography component="p">{language.generate}</Typography>
				</Button>
			</DialogActions>
		</Dialog>
	);
}

ExportColumnDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	setClose: PropTypes.func.isRequired,
	language: PropTypes.object.isRequired,
	client: PropTypes.string,
	pageKey: PropTypes.string.isRequired,
	sections: PropTypes.array.isRequired,
	onGenerate: PropTypes.func.isRequired
};
